import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Lock, Mail, Phone, Calendar, FileText, User, Car, Clock, LogOut, MessageSquare, ClipboardList } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// NOTE: This is a stop-gap measure. The password is still embedded in the client
// bundle at build time. For real security, replace with server-side JWT auth.
const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || "";

export default function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [contacts, setContacts] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [contactsRes, quotesRes] = await Promise.all([
        axios.get(`${API}/contact`),
        axios.get(`${API}/quotes`)
      ]);
      setContacts(contactsRes.data.reverse());
      setQuotes(quotesRes.data.reverse());
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Check session storage for auth
  useEffect(() => {
    const auth = sessionStorage.getItem("martens_admin_auth");
    if (auth === "true") {
      setIsAuthenticated(true);
      fetchData();
    }
  }, [fetchData]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      sessionStorage.setItem("martens_admin_auth", "true");
      setError("");
      fetchData();
    } else {
      setError("Invalid password");
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem("martens_admin_auth");
    setPassword("");
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen pt-20 bg-[#09090b] flex items-center justify-center" data-testid="admin-login-page">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md mx-4"
        >
          <div className="bg-zinc-900 border border-white/10 p-8">
            <div className="flex items-center justify-center w-16 h-16 bg-navy mx-auto mb-6">
              <Lock size={32} className="text-amber-500" />
            </div>
            <h1 className="font-heading text-3xl text-white text-center mb-2">ADMIN ACCESS</h1>
            <p className="font-body text-zinc-400 text-center mb-8">Enter password to continue</p>

            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <Label htmlFor="password" className="font-mono text-xs tracking-wide text-zinc-400 uppercase">
                  PASSWORD
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-2 bg-zinc-950 border-white/10 text-white rounded-none focus:border-amber-500 h-12"
                  placeholder="Enter admin password"
                  data-testid="admin-password-input"
                />
              </div>

              {error && (
                <p className="text-red-500 font-body text-sm">{error}</p>
              )}

              <Button
                type="submit"
                className="w-full bg-amber-500 text-black font-heading text-lg tracking-wide py-6 rounded-none hover:bg-amber-400 transition-all duration-200"
                data-testid="admin-login-btn"
              >
                LOGIN
              </Button>
            </form>
          </div>
        </motion.div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen pt-20 bg-[#09090b]" data-testid="admin-dashboard">
      {/* Header */}
      <section className="py-8 bg-zinc-950 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div>
            <h1 className="font-heading text-3xl text-white">ADMIN DASHBOARD</h1>
            <p className="font-mono text-xs text-zinc-500 mt-1">Manage contact submissions and project inquiries</p>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="border-white/20 text-white font-mono text-sm rounded-none hover:bg-white/5"
            data-testid="admin-logout-btn"
          >
            <LogOut size={16} className="mr-2" />
            LOGOUT
          </Button>
        </div>
      </section>

      {/* Stats */}
      <section className="py-8 bg-[#09090b]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-zinc-900 border border-white/10 p-6">
              <MessageSquare size={24} className="text-amber-500 mb-2" />
              <div className="font-heading text-3xl text-white">{contacts.length}</div>
              <div className="font-mono text-xs text-zinc-500">CONTACT MESSAGES</div>
            </div>
            <div className="bg-zinc-900 border border-white/10 p-6">
              <ClipboardList size={24} className="text-amber-500 mb-2" />
              <div className="font-heading text-3xl text-white">{quotes.length}</div>
              <div className="font-mono text-xs text-zinc-500">PROJECT INQUIRIES</div>
            </div>
            <div className="bg-zinc-900 border border-white/10 p-6">
              <Clock size={24} className="text-amber-500 mb-2" />
              <div className="font-heading text-3xl text-white">{quotes.filter(q => q.status === 'pending').length}</div>
              <div className="font-mono text-xs text-zinc-500">PENDING INQUIRIES</div>
            </div>
            <div className="bg-navy p-6">
              <Calendar size={24} className="text-amber-500 mb-2" />
              <div className="font-heading text-3xl text-amber-500">{contacts.length + quotes.length}</div>
              <div className="font-mono text-xs text-white/70">TOTAL LEADS</div>
            </div>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs defaultValue="quotes" className="w-full">
            <TabsList className="bg-zinc-900 border border-white/10 rounded-none p-1 mb-6">
              <TabsTrigger 
                value="quotes" 
                className="font-mono text-sm data-[state=active]:bg-amber-500 data-[state=active]:text-black rounded-none px-6"
                data-testid="tab-quotes"
              >
                PROJECT INQUIRIES ({quotes.length})
              </TabsTrigger>
              <TabsTrigger 
                value="contacts" 
                className="font-mono text-sm data-[state=active]:bg-amber-500 data-[state=active]:text-black rounded-none px-6"
                data-testid="tab-contacts"
              >
                CONTACT MESSAGES ({contacts.length})
              </TabsTrigger>
            </TabsList>

            {/* Quote Requests Tab */}
            <TabsContent value="quotes">
              {loading ? (
                <div className="text-center py-12">
                  <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto" />
                </div>
              ) : quotes.length === 0 ? (
                <div className="bg-zinc-900 border border-white/10 p-12 text-center">
                  <ClipboardList size={48} className="text-zinc-600 mx-auto mb-4" />
                  <p className="font-body text-zinc-400">No project inquiries yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {quotes.map((quote) => (
                    <motion.div
                      key={quote.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-zinc-900 border border-white/10 p-6 hover:border-amber-500/30 transition-colors"
                      data-testid={`quote-item-${quote.id}`}
                    >
                      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <span className={`px-3 py-1 font-mono text-xs uppercase ${
                              quote.status === 'pending' 
                                ? 'bg-amber-500/20 text-amber-500' 
                                : 'bg-green-500/20 text-green-500'
                            }`}>
                              {quote.status}
                            </span>
                            <span className="font-mono text-xs text-zinc-500">
                              {formatDate(quote.created_at)}
                            </span>
                          </div>
                          
                          <h3 className="font-heading text-xl text-white mb-2">
                            {quote.name}
                          </h3>
                          
                          <div className="grid sm:grid-cols-2 gap-4 mb-4">
                            <div className="flex items-center gap-2 text-zinc-400">
                              <Mail size={14} />
                              <a href={`mailto:${quote.email}`} className="font-body text-sm hover:text-amber-500">
                                {quote.email}
                              </a>
                            </div>
                            {quote.phone && (
                              <div className="flex items-center gap-2 text-zinc-400">
                                <Phone size={14} />
                                <a href={`tel:${quote.phone}`} className="font-body text-sm hover:text-amber-500">
                                  {quote.phone}
                                </a>
                              </div>
                            )}
                          </div>

                          {(quote.vehicle_year || quote.vehicle_make || quote.vehicle_model) && (
                            <div className="flex items-center gap-2 text-amber-500 mb-3">
                              <Car size={16} />
                              <span className="font-body text-sm">
                                {[quote.vehicle_year, quote.vehicle_make, quote.vehicle_model].filter(Boolean).join(' ')}
                              </span>
                            </div>
                          )}

                          <div className="flex items-center gap-2 text-zinc-400 mb-3">
                            <FileText size={14} />
                            <span className="font-mono text-xs uppercase">{quote.service_type.replace('_', ' ')}</span>
                          </div>

                          <p className="font-body text-zinc-300 bg-zinc-950 p-4 border-l-2 border-amber-500">
                            {quote.description}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Contact Messages Tab */}
            <TabsContent value="contacts">
              {loading ? (
                <div className="text-center py-12">
                  <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto" />
                </div>
              ) : contacts.length === 0 ? (
                <div className="bg-zinc-900 border border-white/10 p-12 text-center">
                  <MessageSquare size={48} className="text-zinc-600 mx-auto mb-4" />
                  <p className="font-body text-zinc-400">No contact messages yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {contacts.map((contact) => (
                    <motion.div
                      key={contact.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-zinc-900 border border-white/10 p-6 hover:border-amber-500/30 transition-colors"
                      data-testid={`contact-item-${contact.id}`}
                    >
                      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <User size={16} className="text-amber-500" />
                            <h3 className="font-heading text-xl text-white">
                              {contact.name}
                            </h3>
                            <span className="font-mono text-xs text-zinc-500">
                              {formatDate(contact.created_at)}
                            </span>
                          </div>
                          
                          <div className="grid sm:grid-cols-2 gap-4 mb-4">
                            <div className="flex items-center gap-2 text-zinc-400">
                              <Mail size={14} />
                              <a href={`mailto:${contact.email}`} className="font-body text-sm hover:text-amber-500">
                                {contact.email}
                              </a>
                            </div>
                            {contact.phone && (
                              <div className="flex items-center gap-2 text-zinc-400">
                                <Phone size={14} />
                                <a href={`tel:${contact.phone}`} className="font-body text-sm hover:text-amber-500">
                                  {contact.phone}
                                </a>
                              </div>
                            )}
                          </div>

                          <p className="font-body text-zinc-300 bg-zinc-950 p-4 border-l-2 border-amber-500">
                            {contact.message}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
}
